import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Loader2,
  CheckCircle,
  ArrowRight,
  ClipboardCheck,
  FileEdit,
  GraduationCap,
  Plane,
  Ticket,
  Home,
  Wallet,
  Briefcase,
  Clock,
  XCircle,
  AlertCircle,
  Lock,
  ShieldCheck
} from 'lucide-react';
import { agentAPI } from '../../services/api';
import { toast } from 'sonner';

const SERVICES = [
  {
    type: 'PROFILE_ASSESSMENT',
    name: 'Profile Assessment',
    description: 'Complete profile evaluation and career counseling',
    icon: ClipboardCheck,
    color: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-100'
  },
  {
    type: 'APPLICATION_ASSISTANCE',
    name: 'Pre-application Support',
    description: 'Documentation and application preparation',
    icon: FileEdit,
    color: 'bg-purple-50 border-purple-200 hover:bg-purple-100',
    iconColor: 'text-purple-600',
    iconBg: 'bg-purple-100'
  },
  {
    type: 'UNIVERSITY_SHORTLISTING',
    name: 'Apply University',
    description: 'University selection and application submission',
    icon: GraduationCap,
    color: 'bg-green-50 border-green-200 hover:bg-green-100',
    iconColor: 'text-green-600',
    iconBg: 'bg-green-100'
  },
  {
    type: 'VISA_GUIDANCE',
    name: 'Visa & Interview Support',
    description: 'Visa processing and interview preparation',
    icon: Plane,
    color: 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100',
    iconColor: 'text-yellow-600',
    iconBg: 'bg-yellow-100'
  },
  {
    type: 'PRE_DEPARTURE_ORIENTATION',
    name: 'Ticket & Travel Support',
    description: 'Flight booking and travel arrangements',
    icon: Ticket,
    color: 'bg-orange-50 border-orange-200 hover:bg-orange-100',
    iconColor: 'text-orange-600',
    iconBg: 'bg-orange-100'
  },
  {
    type: 'ACCOMMODATION_HELP',
    name: 'Find Accommodation',
    description: 'Student housing and accommodation search',
    icon: Home,
    color: 'bg-pink-50 border-pink-200 hover:bg-pink-100',
    iconColor: 'text-pink-600',
    iconBg: 'bg-pink-100'
  },
  {
    type: 'LOAN_ASSISTANCE',
    name: 'Education Loan',
    description: 'Financial aid and loan assistance',
    icon: Wallet,
    color: 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100',
    iconColor: 'text-emerald-600',
    iconBg: 'bg-emerald-100'
  },
  {
    type: 'SCHOLARSHIP_SEARCH',
    name: 'Find Jobs Abroad',
    description: 'Job search and career placement',
    icon: Briefcase,
    color: 'bg-cyan-50 border-cyan-200 hover:bg-cyan-100',
    iconColor: 'text-cyan-600',
    iconBg: 'bg-cyan-100'
  },
];

// Approval status badge component
const ApprovalStatusBadge = ({ status }) => {
  const statusConfig = {
    PENDING_APPROVAL: {
      icon: Clock,
      label: 'Pending Approval',
      className: 'bg-amber-100 text-amber-700 border-amber-200'
    },
    APPROVED: {
      icon: ShieldCheck,
      label: 'Approved',
      className: 'bg-green-100 text-green-700 border-green-200'
    },
    REJECTED: {
      icon: XCircle,
      label: 'Rejected',
      className: 'bg-red-100 text-red-700 border-red-200'
    }
  };

  const config = statusConfig[status];
  if (!config) return null;

  const Icon = config.icon;

  return (
    <Badge variant="outline" className={`text-xs gap-1 ${config.className}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

// Service status badge
const ServiceStatusBadge = ({ status }) => {
  const statusStyles = {
    COMPLETED: 'bg-green-50 text-green-700 border-green-200',
    IN_PROGRESS: 'bg-blue-50 text-blue-700 border-blue-200',
    PENDING_ADMIN_ASSIGNMENT: 'bg-amber-50 text-amber-700 border-amber-200',
    ASSIGNED: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    WAITING_STUDENT: 'bg-purple-50 text-purple-700 border-purple-200',
    ON_HOLD: 'bg-gray-50 text-gray-700 border-gray-200',
    CANCELLED: 'bg-red-50 text-red-700 border-red-200'
  };

  return (
    <Badge variant="outline" className={`text-xs ${statusStyles[status] || 'bg-gray-50 text-gray-700'}`}>
      {status.replace(/_/g, ' ')}
    </Badge>
  );
};

export default function StudentServicesTab({ studentId, appliedServices, serviceRequests, onServiceApplied }) {
  const navigate = useNavigate();
  const [applyingService, setApplyingService] = useState(null);

  const handleApplyService = async (serviceType) => {
    try {
      setApplyingService(serviceType);
      await agentAPI.applyServiceForStudent(studentId, serviceType);
      toast.success('Service request submitted! Awaiting Super Admin approval.');
      onServiceApplied();
    } catch (error) {
      console.error('Failed to apply service:', error);
      toast.error(error.response?.data?.error || 'Failed to apply service');
    } finally {
      setApplyingService(null);
    }
  };

  const getServiceRequest = (serviceType) => {
    return serviceRequests?.find(sr => sr.serviceType === serviceType && sr.status !== 'CANCELLED');
  };

  const handleViewDetails = (serviceType) => {
    const serviceRequest = getServiceRequest(serviceType);
    if (serviceRequest) {
      // Check approval status before navigating
      if (serviceRequest.isAgentInitiated && serviceRequest.agentApprovalStatus !== 'APPROVED') {
        if (serviceRequest.agentApprovalStatus === 'PENDING_APPROVAL') {
          toast.warning('This service request is pending approval. You can view details after Super Admin approves.');
        } else if (serviceRequest.agentApprovalStatus === 'REJECTED') {
          toast.error('This service request was rejected and cannot be accessed.');
        }
        return;
      }
      navigate(`/agent/service-requests/${serviceRequest.serviceRequestId}`);
    }
  };

  const canAccessDetails = (serviceRequest) => {
    // If not agent-initiated, allow access
    if (!serviceRequest?.isAgentInitiated) return true;
    // If agent-initiated, only allow if approved
    return serviceRequest.agentApprovalStatus === 'APPROVED';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Available Services</h3>
          <p className="text-sm text-gray-500">
            Apply services for this student. Agent-initiated requests require Super Admin approval.
          </p>
        </div>
      </div>

      {/* Info Banner for Agent Workflow */}
      <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-blue-800 mb-1">Agent Service Request Workflow</p>
          <p className="text-blue-700">
            When you apply a service for your student, it will be sent to Super Admin for approval.
            Once approved, you can access the service details page to manage tasks and workflow.
          </p>
        </div>
      </div>

      {/* Service Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SERVICES.map((service) => {
          const isApplied = appliedServices?.includes(service.type);
          const isApplying = applyingService === service.type;
          const serviceRequest = getServiceRequest(service.type);
          const Icon = service.icon;
          const hasAccess = canAccessDetails(serviceRequest);
          const isPendingApproval = serviceRequest?.isAgentInitiated && serviceRequest?.agentApprovalStatus === 'PENDING_APPROVAL';
          const isRejected = serviceRequest?.isAgentInitiated && serviceRequest?.agentApprovalStatus === 'REJECTED';

          return (
            <Card
              key={service.type}
              className={`p-5 border transition-all ${service.color} ${
                isRejected ? 'opacity-60' : isApplied && !hasAccess ? 'opacity-80' : ''
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${service.iconBg} ${service.iconColor}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h4 className="font-semibold text-gray-900">{service.name}</h4>
                    {isApplied && (
                      <Badge className="bg-green-100 text-green-700 text-xs shrink-0">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Applied
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{service.description}</p>

                  {/* Status Badges */}
                  {serviceRequest && (
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <ServiceStatusBadge status={serviceRequest.status} />
                      {serviceRequest.isAgentInitiated && (
                        <ApprovalStatusBadge status={serviceRequest.agentApprovalStatus} />
                      )}
                    </div>
                  )}

                  {/* Pending Approval Message */}
                  {isPendingApproval && (
                    <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                      <Lock className="w-4 h-4 text-amber-600" />
                      <p className="text-xs text-amber-700">
                        Awaiting Super Admin approval to access service workflow
                      </p>
                    </div>
                  )}

                  {/* Rejected Message */}
                  {isRejected && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      <p className="text-xs text-red-700">
                        Request was rejected. Contact Super Admin for details.
                      </p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    {isApplied ? (
                      <Button
                        size="sm"
                        variant={hasAccess ? 'outline' : 'ghost'}
                        onClick={() => handleViewDetails(service.type)}
                        disabled={!hasAccess || isRejected}
                        className={hasAccess ? 'bg-white/80' : 'opacity-70 cursor-not-allowed'}
                      >
                        {hasAccess ? (
                          <>
                            View Details
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </>
                        ) : (
                          <>
                            <Lock className="w-4 h-4 mr-1" />
                            {isPendingApproval ? 'Pending Approval' : 'Access Denied'}
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleApplyService(service.type)}
                        disabled={isApplying}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white"
                      >
                        {isApplying ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          'Apply Service'
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
